/**
 * @file PendingDeliveryOrders.tsx
 * @module app/modules/pending-delivery-orders/PendingDeliveryOrders
 * @description Implements the PendingDeliveryOrders workflow for pending delivery orders.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  OverlayTrigger,
  Popover,
  Row,
  Tab,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Method from "../../../utils/methods";
import { useAuth } from "../auth";
import APICallService from "../../../api/apiCallService";
import Loader from "../../../Global/loader";
import {
  customerService,
  driver,
  ordersDelivery,
  routePlanning,
} from "../../../api/apiEndPoints";
import {
  Manager,
  OrdersDelivery,
  ORDER_PAGE_LIMIT,
  OrderPlacedByCustomer,
  RouteOrderMovedToPendingOrder,
  ServiceMember,
  View,
  AssistAdmin,
  PendingDeliveryOrder,
} from "../../../utils/constants";
import { IMAGES } from "../../../utils/assetsAndPermissions";
import { deliveryMessageJson } from "../../../api/apiJSON/master";
import { success } from "../../../Global/toast";
import { pendingDeliveryOrder } from "../../../utils/toast";
import { error } from "../../../Global/toast";
import { warehouse as warehouseStoreString } from "../../../utils/storeString";
import { getKey } from "../../../Global/history";
import { useWarehouse } from "../../context/WarehouseContext";
import { CustomSelect } from "../../custom/Select/CustomSelect";
import { useNavigationHistory } from "../../../utils/useNavigationHistory";
const PendingDeliveryOrders = () => {
  const { currentUser, saveCurrentUser } = useAuth();
  const { selectedWarehouse } = useWarehouse();
  const navigate = useNavigate();
  const { navigateTo } = useNavigationHistory();
  const [fetchLoader, setFetchLoader] = useState(true);
  const [selectedOrders, setSelectedOrders] = useState<any[]>([]);
  const [orders, setOrders] = useState<any>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [selected, setSelected] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<any>([]);
  const [selectLoading, setSelectLoading] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [district, setDistrict] = useState<any>(
    currentUser &&
      currentUser.districts &&
      currentUser.districts[0] &&
      currentUser.districts.find((val: any) => val.flag === true).reference
  );
  const [currentDistrict, setCurrentDistrict] = useState<any>(-1);

  const fetchDetails = async (district?: any,) => {
    setLoading(true);
    setFetchLoader(true)
    let params: any = {
      // pageNo: 0,
      // limit: 0,
      sortKey: "_createdAt",
      sortOrder: -1,
      needCount: true,
      listType: 1,
      skip: 0,
      ...(currentUser?.userType == AssistAdmin &&
      district && district !== -1 && { district })
    };
    if (selectedWarehouse?.value) {
      params.warehouse = selectedWarehouse?.value;
    }
    let endpoint = ordersDelivery.list;
    if (
      currentUser?.role.some(
        (obj: any) => obj.type === Manager || obj.type === ServiceMember
      )
    ) {
      endpoint = ordersDelivery.listAssistAdmin;
    }
    let apiService = new APICallService(
      endpoint,
      params,
      "",
      "",
      "",
      "",
      OrdersDelivery,
      selectedWarehouse?.value || null
    );
    let response: any = await apiService.callAPI();
    if (response.records) {
      if (response.total) {
        setTotalRecords(response.total);
      } else {
        setTotalRecords(0);
      }
      setOrders(response.records);
    }
    setFetchLoader(false);
    setLoading(false);
  };
 useEffect(() => {
  if (!Method.hasPermission(PendingDeliveryOrder,View, currentUser)) {
    return window.history.back();
  }
  const isAssistAdmin = currentUser?.userType === AssistAdmin;
  if (isAssistAdmin && !district) return;
  fetchDetails(isAssistAdmin ? district : undefined);
}, [currentUser?.userType, selectedWarehouse?.value, district]);


  // const handleSelectAll = async () => {
  //   console.log(selected)
  //   if (selected) {
  //     setSelectLoading(true);
  //     setSelectedId([]);

  //     setSelected(false);
  //     setSelectLoading(false);
  //   } else {
  //     setSelectLoading(true);
  //     let allOrderIds = orders?.map((order:any) => order._id);
  //     setSelectedId(allOrderIds);
  //     setSelected(true);
  //     setSelectLoading(false);
  //   }
  // };

  const handleSelectAll = async () => {
    setSelectLoading(true);
    if (selected) {
      setSelectedId([]);
      setSelectedOrders([]);
      setSelected(false);
    } else {
      let allOrderIds = orders?.map((order: any) => order._id);
      setSelectedId(allOrderIds);

      const allSelectedOrders = allOrderIds.map((orderId: any) => {
        const selectedOrder = orders?.find((o: any) => o._id === orderId);
        return {
          buyer: selectedOrder?.customer?.reference,
          refKeys: selectedOrder ? [selectedOrder.refKey] : [],
        };
      });

      const uniqueOrders = allSelectedOrders.reduce(
        (acc: any[], current: any) => {
          const x = acc.find((item) => item.buyer === current.buyer);
          if (!x) {
            return acc.concat([current]);
          } else {
            x.refKeys = Array.from(new Set([...x.refKeys, ...current.refKeys]));
            return acc;
          }
        },
        []
      );

      setSelectedOrders(uniqueOrders);
      setSelected(true);
    }
    setSelectLoading(false);
  };
  const handleChecked = (id: string) => {
    let temp = [...selectedId];

    if (temp.includes(id)) {
      temp = temp.filter((item) => item !== id);
    } else {
      temp.push(id);
    }
    setSelectedId(temp);
    const newSelectedOrders = temp.map((orderId) => {
      const selectedOrder = orders?.find((o: any) => o._id === orderId);
      return {
        buyer: selectedOrder?.customer?.reference,
        refKeys: selectedOrder ? [selectedOrder.refKey] : [],
      };
    });

    const uniqueOrders = newSelectedOrders.reduce((acc: any[], current) => {
      const x = acc.find((item) => item.buyer === current.buyer);
      if (!x) {
        return acc.concat([current]);
      } else {
        x.refKeys = Array.from(new Set([...x.refKeys, ...current.refKeys]));
        return acc;
      }
    }, []);

    setSelectedOrders(uniqueOrders);
    setSelected(temp.length === totalRecords);
  };

  const kgToTons = (kilograms: number) => {
    // Conversion factor
    const conversionFactor = 1000;
    // Calculate metric tons
    const tons = kilograms / conversionFactor;
    return tons.toFixed(3);
  };

  const popoverPartialOrder = (
    <Popover id="popover-basic">
      <Popover.Body className="p-2 bg-black border-r10px text-white">
        <span>Some goods are remaining to be delivered.</span>
      </Popover.Body>
    </Popover>
  );

  const groupAndSortOrders = (orders: any) => {
    const groupedOrders = orders?.reduce((acc: any, order: any) => {
      const ref = order.customer.reference;
      if (!acc[ref]) {
        acc[ref] = [];
      }
      acc[ref].push(order);
      return acc;
    }, {});

    const sortedGroupedOrders = Object.keys(groupedOrders)
      .map((ref) => ({
        reference: ref,
        orders: groupedOrders[ref],
      }))
      .sort((a, b) => {
        const first: any = new Date(a.orders[0]._createdAt);
        const secound: any = new Date(b.orders[0]._createdAt);
        return secound - first;
      });

    return sortedGroupedOrders;
  };
  const handleSendNotification = async () => {
    if (selectedOrders.length === 0 && selectedId.length === 0) {
      error(pendingDeliveryOrder.checkItem);
    } else {
      setLoading(true);
      let apiService = new APICallService(
        ordersDelivery.pendingDeliveryOrder,
        deliveryMessageJson.message({ order: selectedOrders }),
        "",
        "",
        "",
        "",
        0,
        selectedWarehouse?.value || null
      );
      let response = await apiService.callAPI();
      if (response) {
        setSelectedId([]);
        setSelectedOrders([]);
        success(pendingDeliveryOrder.sendDeliveryMessage);
        
      }
    }
  };

  const handleDistricts = async (event: any) => {
    setDistrict(event.value);
    setCurrentDistrict(event.value);
    setFetchLoader(true);
    let user: any = { ...currentUser };
    for (var i = 0; i < user.districts.length; i++) {
      if (user.districts[i].reference === event.value) {
        user.districts[i].flag = true;
      } else {
        user.districts[i].flag = false;
      }
    }
    saveCurrentUser(user);
    setFetchLoader(false);
  };

  return (
    <>
      <div className="p-5">
        <Row className="align-items-center g-md-5 g-3 mb-7">
          <Col xs>
            <h1 className="fs-22 fw-bolder">Pending Delivery Orders</h1>
          </Col>
          {currentUser?.userType == AssistAdmin && 
            <Col xs="auto">
              <CustomSelect
                value={currentUser?.districts.map((val: any) => {
                  if (val.reference === district) {
                    return {
                      label: val.name,
                      value: val.reference,
                      title: val.name,
                    };
                  }
                })}
                backgroundColor="#f9f9f9"
                minHeight="50px"
                minWidth="224px"
                controlFontSize="1.231rem"
                onChange={(event: any) => {
                  handleDistricts(event);
                }}
                options={currentUser?.districts.map((val: any) => {
                  return {
                    label: val.name,
                    value: val.reference,
                    title: val.name,
                  };
                })}
              />
            </Col>
          }
          {selectedOrders.length != 0 && selectedId.length != 0 && (
            <Col xs={"auto"}>
              <Button
                variant="primary"
                className="uw-btn uw-btn-primary uw-btn-lg fs-16 fw-bold btn-lg"
                onClick={handleSendNotification}
              >
                Send Message
              </Button>
            </Col>
          )}
        </Row>
        <Row>
          <Col xs={12}>
            <Col xs={12}>
              {!fetchLoader ? (
                orders && orders?.length ? (
                  <>
                    <Card className="border border-r10px">
                      <Card.Body className="p-0">
                        <div className="table-responsive">
                          <table className="table table-rounded table-row-bordered align-middle gs-7 gy-6 mb-0">
                            <thead>
                              <tr className="fw-bold fs-16 fw-600 text-dark border-bottom align-middle">
                                <th
                                  className="min-w-100px"
                                  tabIndex={0}
                                  rowSpan={1}
                                  colSpan={1}
                                >
                                  <div className="form-check form-check-sm form-check-custom form-check-solid">
                                    <input
                                      className="form-check-input h-30px w-30px"
                                      type="checkbox"
                                      value="1"
                                      checked={
                                        selectedId.length === totalRecords &&
                                        totalRecords > 0
                                      }
                                      onChange={(event: any) => {
                                        handleSelectAll();
                                      }}
                                    />
                                  </div>
                                </th>

                                <th className="min-w-md-175px min-w-150px">
                                  Order Date & ID
                                </th>

                                <th className="min-w-md-200px min-w-125px">
                                  Customer name
                                </th>
                                {currentUser?.role.length ? (
                                  currentUser?.role.some(
                                    (obj: any) =>
                                      obj.type !== Manager ||
                                      obj.type !== ServiceMember
                                  ) ? (
                                    <></>
                                  ) : (
                                    <th className="min-w-140px">Ordered by</th>
                                  )
                                ) : (
                                  <></>
                                )}
                                <th className="min-w-md-125px">Quantity</th>
                                <th className="min-w-md-125px">Weight</th>
                                <th className="min-w-md-125px min-w-100px">
                                  Total amount
                                </th>
                                <th className="min-w-md-75px min-w-50px">
                                  Moved To Pending
                                </th>
                                <th className="min-w-100px text-end"></th>
                              </tr>
                            </thead>
                            <tbody>
                              {selectLoading ? (
                                <tr>
                                  <td colSpan={6}>
                                    <div className="d-flex justify-content-center text-center align-items-center min-h-160px px-3 fs-20 text-dark fw-600">
                                      Please wait while processing...
                                    </div>
                                  </td>
                                </tr>
                              ) : (
                                <>
                                  {groupAndSortOrders(orders)?.map(
                                    (group, groupIndex) =>
                                      group?.orders?.map(
                                        (orderVal: any, goodIndex: number) => (
                                          <tr key={goodIndex}>
                                            <td>
                                              <div className="form-check form-check-sm form-check-custom form-check-solid">
                                                <input
                                                  className="form-check-input h-30px w-30px"
                                                  type="checkbox"
                                                  value={orderVal._id}
                                                  checked={selectedId.includes(
                                                    orderVal._id
                                                  )}
                                                  onChange={() => {
                                                    handleChecked(orderVal._id);
                                                  }}
                                                />
                                              </div>
                                            </td>

                                            <td className="fs-15 fw-500">
                                              <div className="d-flex align-items-start flex-column">
                                                <div className="d-flex flex-row">
                                                  {orderVal.totalQuantities.reduce(
                                                    (
                                                      total: number,
                                                      item: any
                                                    ) =>
                                                      total + item.stockCount,
                                                    0
                                                  ) >
                                                    orderVal.mProcessedQuantities &&
                                                  orderVal.mProcessedQuantities !=
                                                    0 ? (
                                                    <OverlayTrigger
                                                      trigger="hover"
                                                      placement="bottom-start"
                                                      overlay={
                                                        popoverPartialOrder
                                                      }
                                                    >
                                                      <img
                                                        className="me-2"
                                                        width={28}
                                                        height={21}
                                                        src={
                                                          IMAGES.PartialTruckTwo
                                                        }
                                                        alt=""
                                                      />
                                                    </OverlayTrigger>
                                                  ) : (
                                                    <></>
                                                  )}
                                                  <span className="text-dark d-block">
                                                    {Method.convertDateToDDMMYYYYHOURS(
                                                      orderVal._createdAt
                                                    )}
                                                  </span>
                                                </div>
                                                <span className="text-gray d-block">
                                                  {orderVal.refKey}
                                                </span>
                                              </div>
                                            </td>

                                            <td>
                                              <span className="fs-15 fw-500 d-block">
                                                {orderVal.customer.name}
                                              </span>
                                            </td>
                                            {currentUser?.role.length ? (
                                              currentUser?.role.some(
                                                (obj: any) =>
                                                  obj.type !== Manager ||
                                                  obj.type !== ServiceMember
                                              ) ? (
                                                <></>
                                              ) : (
                                                <td>
                                                  <span className="badge badge-light border-r23px p-4 fs-15 fw-600 text-dark">
                                                    {orderVal.placedBy.type ===
                                                    OrderPlacedByCustomer
                                                      ? "Customer"
                                                      : orderVal.placedBy.user
                                                          .name}
                                                  </span>
                                                </td>
                                              )
                                            ) : (
                                              <></>
                                            )}
                                            <td>
                                              <span className="fs-15 fw-500">
                                                {/* {goodVal.quantity + ' items'} */}
                                                {orderVal.totalQuantities.reduce(
                                                  (total: number, item: any) =>
                                                    total + item.stockCount,
                                                  0
                                                ) -
                                                  orderVal.mProcessedQuantities +
                                                  " items"}
                                              </span>
                                            </td>
                                            <td>
                                              <span className="fs-15 fw-500">
                                                {/* {goodVal.quantity + ' items'} */}
                                                {kgToTons(
                                                  orderVal.totalQuantities.reduce(
                                                    (
                                                      total: number,
                                                      item: any
                                                    ) => total + item.weight,
                                                    0
                                                  ) - orderVal.mProcessedWeight
                                                ) + " tons"}
                                              </span>
                                            </td>
                                            <td>
                                              <span className="fs-15 fw-500">
                                                {"TSh " +
                                                  Method.getGeneralizedAmount(
                                                    orderVal.payment.totalCharge
                                                  )}
                                              </span>
                                            </td>
                                            <td>
                                              {orderVal.routesVehicles &&
                                              orderVal.routesVehicles.length &&
                                              orderVal.routesVehicles[0]
                                                .status ==
                                                RouteOrderMovedToPendingOrder ? (
                                                <>
                                                  <span className="badge badge-danger fs-14 fw-600 text-light p-3 border-r4px me-5">
                                                    Yes -{" "}
                                                    {Method.convertDateToDDMMYYYYHOURS(
                                                      orderVal.routesVehicles[0]
                                                        .statusUpdatedAt
                                                    )}
                                                  </span>
                                                </>
                                              ) : (
                                                <>
                                                  <span className="badge badge-light fs-14 fw-600 text-dark p-3 border-r4px me-5">
                                                    No
                                                  </span>
                                                </>
                                              )}
                                            </td>
                                            <td className="text-end">
                                              {Method.hasPermission(
                                                OrdersDelivery,
                                                View,
                                                currentUser
                                              ) ? (
                                                <Button
                                                  className="uw-btn uw-btn-primary fs-14 fw-600"
                                                  onClick={() => {
                                                    let currentScrollY =
                                                      window.scrollY;
                                                    navigateTo(
                                                      "/orders/order-details",
                                                      {
                                                        state: {
                                                          id: orderVal._id,
                                                          moduleName:
                                                            OrdersDelivery,
                                                        },
                                                      }
                                                    );
                                                  }}
                                                >
                                                  View details
                                                </Button>
                                              ) : (
                                                <></>
                                              )}
                                            </td>
                                          </tr>
                                        )
                                      )
                                  )}
                                </>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </Card.Body>
                    </Card>
                  </>
                ) : (
                  <>
                    <div className="border border-r10px mb-6">
                      <div className="d-flex justify-content-center text-center align-items-center min-h-160px px-3">
                        <span className="fs-18 fw-500">No Data Found</span>
                      </div>
                    </div>
                  </>
                )
              ) : (
                <div className="border border-r10px mb-6">
                  <div className="d-flex justify-content-center text-center align-items-center min-h-160px px-3">
                    <Loader loading={fetchLoader || loading} />
                  </div>
                </div>
              )}
            </Col>
          </Col>
        </Row>
      </div>
    </>
  );
};
export default PendingDeliveryOrders;
